import type {
  ArticleSuggestion,
  CallSection,
  ChatSuggestion,
  CoachingTip,
  MetaField,
  NextStep,
  Nudge,
} from "@repo/app-types/entities/liveCoaching";
import { IconChevronDown, IconChevronUp } from "@tabler/icons-react";
import { Duration } from "luxon";
import { useEffect, useMemo, useState, type FC, type ReactNode } from "react";
import {
  Tab,
  TabList,
  TabPanel,
  Tabs,
  ToggleButton,
} from "react-aria-components";
import { ArticleOverlay } from "./articleOverlay.js";
import { ArticleSuggestionsWidget } from "./articleSuggestionsWidget.js";
import { ChatSuggestionsWidget } from "./chatSuggestionsWidget.js";
import { CoachingContent } from "./coachingContent.js";
import { SummaryContent } from "./summaryContent.js";
import { TranscriptContent, type Utterance } from "./transcriptContent.js";

export type CallStatus = "idle" | "started" | "ended";

export interface LiveCoachingWidgetProps {
  /** Current status of the call. */
  callStatus: CallStatus;
  /** Current time/duration of the call in seconds. 0 if the call hasn't started. */
  callTimeSeconds: number;
  /** Current nudge to display, if any. */
  activeNudge: Nudge | undefined;
  /** Call sections and their progress. */
  callSections: readonly CallSection[];
  /** Current article suggestions to display, if any. */
  articleSuggestions: readonly ArticleSuggestion[];
  /** Fetches an article suggestion's body after the user selects it. */
  onArticleSuggestionOpen: (
    suggestion: ArticleSuggestion,
    signal: AbortSignal,
  ) => Promise<{ body: string }>;
  /** Current chat suggestions to display, if any. */
  chatSuggestions: readonly ChatSuggestion[];
  onChatSuggestionClick: (suggestion: ChatSuggestion) => void;
  /** Number of chat messages in the chat thread. */
  chatMessagesCount: number;
  /** Renderer for a scrollable container, such as a <ThreadPrimitive.Viewport>. */
  renderChatMessagesContainer: (props: { className: string }) => ReactNode;
  /** Rendered chat composer, such as a styled <ComposerPrimitive.Root>. */
  chatComposer: ReactNode;
  transcriptUtterances: readonly Utterance[];
  /** Post-call coaching tips if/when available. */
  coachingTips: readonly CoachingTip[] | undefined;
  /** Post-call summary if/when available. */
  summaryMarkdown: string | undefined;
  /** Post-call next steps if/when available. */
  nextSteps: readonly NextStep[] | undefined;
  /** Post call meta data fields if/when available. */
  metaFields: readonly MetaField[] | undefined;
}

export const LiveCoachingWidget: FC<LiveCoachingWidgetProps> = (props) => {
  const {
    openArticleSuggestion,
    onArticleSuggestionClick,
    onArticleSuggestionClose,
  } = useArticleSuggestions(props);

  const [isChatExpanded, setIsChatExpanded] = useState<boolean>(false);

  const onChatExpandedToggle = () =>
    setIsChatExpanded((isExpanded) => !isExpanded);

  // Auto expand the chat messages when the message count increases
  useEffect(() => {
    if (props.chatMessagesCount > 0) {
      setIsChatExpanded(true);
    }
  }, [props.chatMessagesCount]);

  const callTime = Duration.fromObject({ seconds: props.callTimeSeconds });

  const agentTalkRatio = useMemo(() => {
    let agentSpeechTime = 0;
    let totalSpeechTime = 0;

    for (const utterance of props.transcriptUtterances) {
      const speechTime = utterance.endTimeSeconds - utterance.startTimeSeconds;

      if (utterance.role === "agent") {
        agentSpeechTime += speechTime;
      }

      totalSpeechTime += speechTime;
    }

    if (totalSpeechTime === 0) {
      return undefined;
    }

    return agentSpeechTime / totalSpeechTime;
  }, [props.transcriptUtterances]);

  const onCollapseChatClick = () => {
    if (isChatExpanded) {
      setIsChatExpanded(false);
    }
  };

  const onComposerFocus = () => {
    if (props.chatMessagesCount > 0) {
      setIsChatExpanded(true);
    }
  };

  return (
    <div className="[container-type:size] relative flex h-full flex-auto flex-col bg-white">
      <div
        className="isolate grid min-w-[320px] flex-auto grid-cols-[minmax(0,1fr)] grid-rows-[auto_minmax(0,1fr)_auto]"
        onClick={onCollapseChatClick}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border-light bg-gradient-to-r from-indigo-50 to-white px-4 py-3">
          <div className="flex items-center gap-2">
            <svg
              className="size-4 text-foreground"
              viewBox="0 0 64 64"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M45.8552 16.3019C45.9684 16.1145 46.1713 16 46.3902 16H62.8925C63.3791 16 63.6791 16.5316 63.4275 16.9481L44.8539 47.6981C44.7407 47.8855 44.5378 48 44.3189 48H27.8167C27.33 48 27.0301 47.4684 27.2817 47.0519L45.8552 16.3019Z"
                fill="currentColor"
              />
              <path
                d="M37.0833 16.3019C37.1964 16.1145 37.3994 16 37.6182 16H40.6557C41.1423 16 41.4422 16.5316 41.1906 16.9481L22.6171 47.6981C22.5039 47.8855 22.301 48 22.0821 48H19.0447C18.5581 48 18.2581 47.4684 18.5097 47.0519L37.0833 16.3019Z"
                fill="currentColor"
              />
              <path
                d="M28.115 16.3019C28.2282 16.1145 28.4311 16 28.65 16H31.6874C32.174 16 32.474 16.5316 32.2224 16.9481L13.6488 47.6981C13.5357 47.8855 13.3327 48 13.1138 48H10.0764C9.58979 48 9.28984 47.4684 9.54144 47.0519L28.115 16.3019Z"
                fill="currentColor"
              />
              <path
                d="M19.1463 16.3019C19.2594 16.1145 19.4624 16 19.6812 16H22.7187C23.2053 16 23.5052 16.5316 23.2536 16.9481L4.68008 47.6981C4.56691 47.8855 4.36398 48 4.14509 48H1.10768C0.621043 48 0.321093 47.4684 0.572693 47.0519L19.1463 16.3019Z"
                fill="currentColor"
              />
            </svg>
            <p className="text-sm font-semibold text-foreground">
              {props.callStatus === "ended" ? "Call complete" : "Assist"}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {props.callStatus === "idle" ? (
              <span className="size-2 rounded-full bg-slate-300" />
            ) : props.callStatus === "started" ? (
              <span className="size-2 animate-pulse rounded-full bg-emerald-400" />
            ) : null}

            <p className="text-sm font-semibold text-foreground tabular-nums">
              {callTime.toFormat(
                callTime.as("hours") >= 1 ? "h:mm:ss" : "mm:ss",
              )}
            </p>
          </div>
        </div>

        {/* Main */}
        <Tabs className="flex flex-col">
          <TabList className="flex border-b border-border-light">
            <Tab
              id="main"
              className="flex-auto cursor-pointer py-2 text-center text-xs font-semibold text-slate-400 transition-colors hover:text-slate-600 selected:border-b-2 selected:border-indigo-500 selected:text-indigo-600"
            >
              {props.callStatus === "ended" ? "Summary" : "Coaching"}
            </Tab>
            <Tab
              id="transcript"
              className="flex-auto cursor-pointer py-2 text-center text-xs font-semibold text-slate-400 transition-colors hover:text-slate-600 selected:border-b-2 selected:border-indigo-500 selected:text-indigo-600"
            >
              Transcript
            </Tab>
          </TabList>

          <TabPanel id="main" className="flex flex-auto flex-col">
            {props.callStatus === "ended" ? (
              <SummaryContent
                coachingTips={props.coachingTips}
                summaryMarkdown={props.summaryMarkdown}
                nextSteps={props.nextSteps}
                metaFields={props.metaFields}
              />
            ) : (
              <CoachingContent
                callTimeSeconds={props.callTimeSeconds}
                agentTalkRatio={agentTalkRatio}
                activeNudge={props.activeNudge}
                callStages={props.callSections}
              />
            )}
          </TabPanel>
          <TabPanel id="transcript" className="flex flex-auto flex-col">
            <TranscriptContent
              isAutoScrollActive={props.callStatus === "started"}
              utterances={props.transcriptUtterances}
            />
          </TabPanel>
        </Tabs>

        {/* Footer */}
        <div className="border-t border-border-light py-1.5">
          {/* Article suggestions */}
          {props.articleSuggestions.length > 0 ? (
            <div className="px-4 py-1.5">
              <ArticleSuggestionsWidget
                articleSuggestions={props.articleSuggestions}
                onAction={onArticleSuggestionClick}
              />
            </div>
          ) : null}

          {/* Chat */}
          <div className="">
            {isChatExpanded ||
            props.chatSuggestions.length > 0 ||
            props.chatMessagesCount > 0 ? (
              <div className="flex items-center justify-between gap-4 px-4 py-1.5">
                {/* Chat header / suggestions */}
                {isChatExpanded || props.chatSuggestions.length === 0 ? (
                  <div className="py-1 text-[10px] leading-4 font-semibold text-slate-400">
                    Chat
                  </div>
                ) : (
                  <ChatSuggestionsWidget
                    chatSuggestions={props.chatSuggestions}
                    onChatSuggestionClick={props.onChatSuggestionClick}
                  />
                )}

                {isChatExpanded || props.chatMessagesCount > 0 ? (
                  <ToggleButton
                    className="flex items-center gap-0.5 rounded-md p-0.5 text-[10px] font-medium text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
                    onClick={onChatExpandedToggle}
                  >
                    {isChatExpanded ? (
                      <IconChevronDown className="size-4" />
                    ) : (
                      <>
                        <IconChevronUp className="size-3" />
                        {props.chatMessagesCount}
                      </>
                    )}
                  </ToggleButton>
                ) : null}
              </div>
            ) : null}

            {isChatExpanded && props.chatMessagesCount > 0 ? (
              <div onClick={(e) => e.stopPropagation()}>
                {props.renderChatMessagesContainer({
                  className: "max-h-[47cqh] overflow-y-auto px-4 py-1.5",
                })}
              </div>
            ) : null}

            {props.chatComposer ? (
              <div
                className="px-4 py-1.5"
                onClick={(e) => e.stopPropagation()}
                onFocus={onComposerFocus}
              >
                {props.chatComposer}
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {openArticleSuggestion !== undefined ? (
        <div className="absolute inset-0 flex flex-col overflow-hidden bg-white">
          <ArticleOverlay
            title={openArticleSuggestion.title}
            summary={openArticleSuggestion.summary}
            body={openArticleSuggestion.body}
            isBodyLoading={openArticleSuggestion.isBodyLoading}
            onClose={onArticleSuggestionClose}
          />
        </div>
      ) : null}
    </div>
  );
};

function useArticleSuggestions(props: {
  articleSuggestions: readonly ArticleSuggestion[];
  onArticleSuggestionOpen: (
    suggestion: ArticleSuggestion,
    signal: AbortSignal,
  ) => Promise<{ body: string }>;
}) {
  const [openArticleSuggestion, setOpenArticleSuggestion] = useState<
    | (ArticleSuggestion & {
        body: string | undefined;
        isBodyLoading: boolean;
        abortController: AbortController;
      })
    | undefined
  >();

  const onArticleSuggestionClick = (suggestion: ArticleSuggestion) => {
    const abortController = new AbortController();

    setOpenArticleSuggestion({
      ...suggestion,
      body: undefined,
      isBodyLoading: true,
      abortController,
    });

    props
      .onArticleSuggestionOpen(suggestion, abortController.signal)
      .then(({ body }) => {
        abortController.signal.throwIfAborted();

        setOpenArticleSuggestion((prev) => {
          if (prev?.abortController !== abortController) {
            return;
          }

          return {
            ...prev,
            body,
            isBodyLoading: false,
          };
        });
      })
      .catch((error) => {
        // TODO: Handle errors.
      });
  };

  const onArticleSuggestionClose = () => {
    setOpenArticleSuggestion(undefined);
  };

  useEffect(
    () => () => openArticleSuggestion?.abortController.abort(),
    [openArticleSuggestion],
  );

  return {
    openArticleSuggestion,
    onArticleSuggestionClick,
    onArticleSuggestionClose,
  };
}
